import { observer } from 'mobx-react-lite';
import React from 'react';
import { Popup } from 'semantic-ui-react';
import { getPercentage } from '../../../../stores/utils';
import { ItemTitle, SVGCircle } from '../../index';
import { Chart, ChartContainer, LaneLabel } from '../Styles';
import { formatCommas } from '../common';

export const LaneProductsStatused = observer(
  ({ auctionID, itemName, laneMetricList, onClick, startTime }) => {
    let pct, showRedChart;

    const getHours = (value) => Math.floor(value / 60);
    const getMinutes = (value) => Math.floor(value % 60);
    const getSeconds = (value) => Math.floor((value * 60) % 60);

    const formatMinuteText = (value) => `${value} min`;
    const formatSecondText = (value) => `${value} sec`;
    const formatHourText = (value) => `${value} hour${value === 1 ? '' : 's'}`;

    const getRunningAverageTimePerUnit = (timeDiffInMinutes, totalUnitsRun) => {
      const timePerUnitRaw = timeDiffInMinutes / totalUnitsRun;
      const hours = getHours(timePerUnitRaw);
      const minutes = getMinutes(timePerUnitRaw);
      const seconds = getSeconds(timePerUnitRaw);
      if (hours === 0 && minutes === 0) return formatSecondText(seconds);
      if (hours === 0)
        return `${formatMinuteText(minutes)} ${formatSecondText(seconds)}`;
      return `${formatHourText(hours)} ${formatMinuteText(
        minutes,
      )} ${formatSecondText(seconds)}`;
    };

    const getTimeDiff = (start, totalUnitsRun) => {
      if (totalUnitsRun === 0) return '';
      const dtCurrent = new Date().getTime();
      const dtStart = new Date(start).getTime();
      const diff = (dtStart - dtCurrent) / 1000 / 60;
      const timeDiffInMinutes = Math.abs(Math.round(diff));
      const averageTimePerUnit = getRunningAverageTimePerUnit(
        timeDiffInMinutes,
        totalUnitsRun,
      );
      return `The average time/unit since start of the auction is ${averageTimePerUnit}.`;
    };

    const getPopupContent = (
      lane,
      auction,
      rangeText,
      percent,
      avgTimePerUnit,
    ) => {
      const auctionOrLane = lane ? `${lane} lane` : `${auction} auction`;
      const formattedTotalCompleted = rangeText.replace('/', ' out of ');
      const status = `${percent}% complete`;
      return `The ${auctionOrLane} is ${status} with a total of ${formattedTotalCompleted} products statused. ${avgTimePerUnit} You may click the circle to view all these products.`;
    };

    return (
      <>
        <ItemTitle
          testId="media-upload-label"
          textSize={1.5}
          titleText="Total Products Statused"
        />
        <ChartContainer>
          {laneMetricList == null || laneMetricList.length == 0 ? (
            <LaneLabel>
              No per Lane Products Status Available for this Auction
            </LaneLabel>
          ) : (
            laneMetricList?.map((item) => {
              pct = getPercentage(
                item.productStatusedCount,
                item.productTotalCount,
              );
              showRedChart = pct < 100 && pct > 95;
              const rangeText =
                formatCommas(item.productStatusedCount) +
                '/' +
                formatCommas(item.productTotalCount);
              return (
                <Chart
                  data-testid="auction-lane-chart-container"
                  key={`chart_${item.laneID}`}
                >
                  <SVGCircle
                    popupContent={getPopupContent(
                      item.laneName,
                      item.auctionName,
                      rangeText,
                      pct,
                      getTimeDiff(startTime, item.productStatusedCount),
                    )}
                    testId={`auction-lane-product-statused-${item.laneID}`}
                    key={`svgcircle_${item.laneID}`}
                    auctionName={itemName}
                    laneName={item.laneName}
                    text={rangeText}
                    percentvalue={pct}
                    chartError={showRedChart}
                    auctionID={auctionID}
                    laneID={item.laneID}
                    onClick={() => onClick(item)}
                    popupHeader="Product Status"
                    popupPosition="left center"
                    clickRoute={`/auction/${auctionID}/lane/${item.laneID}/products`}
                  />

                  <Popup
                    data-testid="auction-lane-products-popup"
                    key={`prod_popup_${item.laneName}`}
                    content={`The ${
                      item.laneName
                    } lane has a total of ${formatCommas(
                      item.laneTotalUnits,
                    )} units, which you can click to view a list of them.`}
                    header="Lane Products"
                    size="tiny"
                    trigger={
                      <LaneLabel
                        data-testid="auction-lane-products-wrapper"
                        style={{ cursor: 'pointer' }}
                        bold
                        onClick={() => onClick(item)}
                      >
                        <h3>{formatCommas(item.laneTotalUnits)} UNITS</h3>
                        {item.laneName}
                      </LaneLabel>
                    }
                  />
                </Chart>
              );
            })
          )}
        </ChartContainer>
      </>
    );
  },
);

export default LaneProductsStatused;
